import {
  FormEvent,
  KeyboardEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react"
import { useAutocompleteSuggestions } from "@/hooks/useAutocompleteSuggestions"
import { Location } from "@/reducers/locationsSlice"
import { createPortal } from "react-dom"
import { useMapsLibrary } from "@vis.gl/react-google-maps"
import { useDebounce } from "use-debounce"
import { useTranslation } from "react-i18next"
import {
  Building2,
  Bus,
  Car,
  Church,
  Coffee,
  Dumbbell,
  Fuel,
  Globe,
  GraduationCap,
  Hotel,
  Landmark,
  MapPin,
  Mountain,
  Plane,
  Scissors,
  ShoppingBag,
  Stethoscope,
  Ticket,
  TrainFront,
  Trees,
  Truck,
  UtensilsCrossed,
  Warehouse,
  Wine,
  type LucideIcon,
} from "lucide-react"

type AutocompleteGooglePlacesProps = {
  onLocationSelect?: (location: Location) => void
  onPlaceSelect?: (place: google.maps.places.Place) => void
  onAbort?: () => void
  initialValue?: string
  placeholder?: string
  autoFocus?: boolean
}

const PLACE_TYPE_ICON: Record<string, LucideIcon> = {
  cafe: Coffee,
  coffee_shop: Coffee,
  tea_house: Coffee,
  restaurant: UtensilsCrossed,
  bakery: UtensilsCrossed,
  deli: UtensilsCrossed,
  meal_delivery: UtensilsCrossed,
  meal_takeaway: UtensilsCrossed,
  food_court: UtensilsCrossed,
  diner: UtensilsCrossed,
  bistro: UtensilsCrossed,
  bar: Wine,
  pub: Wine,
  brewery: Wine,
  winery: Wine,
  beer_garden: Wine,
  airport: Plane,
  international_airport: Plane,
  heliport: Plane,
  train_station: TrainFront,
  subway_station: TrainFront,
  light_rail_station: TrainFront,
  tram_stop: TrainFront,
  bus_station: Bus,
  bus_stop: Bus,
  transit_station: Bus,
  gas_station: Fuel,
  electric_vehicle_charging_station: Fuel,
  truck_stop: Truck,
  truck_dealer: Truck,
  moving_company: Truck,
  courier_service: Truck,
  shipping_service: Truck,
  storage: Warehouse,
  hotel: Hotel,
  motel: Hotel,
  hostel: Hotel,
  lodging: Hotel,
  resort_hotel: Hotel,
  inn: Hotel,
  campground: Hotel,
  bed_and_breakfast: Hotel,
  guest_house: Hotel,
  school: GraduationCap,
  university: GraduationCap,
  library: GraduationCap,
  primary_school: GraduationCap,
  secondary_school: GraduationCap,
  educational_institution: GraduationCap,
  hospital: Stethoscope,
  doctor: Stethoscope,
  dentist: Stethoscope,
  pharmacy: Stethoscope,
  drugstore: Stethoscope,
  city_hall: Landmark,
  courthouse: Landmark,
  government_office: Landmark,
  local_government_office: Landmark,
  post_office: Landmark,
  embassy: Landmark,
  police: Landmark,
  fire_station: Landmark,
  gym: Dumbbell,
  fitness_center: Dumbbell,
  stadium: Dumbbell,
  arena: Dumbbell,
  church: Church,
  mosque: Church,
  synagogue: Church,
  park: Trees,
  national_park: Trees,
  state_park: Trees,
  garden: Trees,
  botanical_garden: Trees,
  mountain_peak: Mountain,
  hiking_area: Mountain,
  beach: Mountain,
  movie_theater: Ticket,
  amusement_park: Ticket,
  museum: Ticket,
  tourist_attraction: Ticket,
  zoo: Ticket,
  aquarium: Ticket,
  car_dealer: Car,
  car_rental: Car,
  car_repair: Car,
  car_wash: Car,
  hair_salon: Scissors,
  beauty_salon: Scissors,
  barber_shop: Scissors,
  spa: Scissors,
  corporate_office: Building2,
  business_center: Building2,
  coworking_space: Building2,
  manufacturer: Building2,
  supplier: Building2,
  country: Globe,
  continent: Globe,
  locality: Globe,
}

const getPlaceTypeIcon = (types: string[]): LucideIcon => {
  for (const type of types) {
    if (PLACE_TYPE_ICON[type]) return PLACE_TYPE_ICON[type]
    if (type.endsWith("_restaurant")) return UtensilsCrossed
    if (type.endsWith("_store") || type.endsWith("_shop")) return ShoppingBag
    if (type.endsWith("_bar") || type.endsWith("_pub")) return Wine
    if (type.endsWith("_school")) return GraduationCap
    if (type.includes("parking")) return Car
    if (type.includes("medical") || type.includes("clinic")) return Stethoscope
    if (type.includes("temple") || type.includes("shrine")) return Church
    if (type.includes("sports")) return Dumbbell
    if (type.startsWith("administrative_area")) return Globe
  }
  if (types.includes("establishment")) return Building2
  return MapPin
}

const BUSINESS_TYPES = new Set([
  "restaurant",
  "cafe",
  "coffee_shop",
  "bakery",
  "bar",
  "pub",
  "brewery",
  "winery",
  "meal_delivery",
  "meal_takeaway",
  "hotel",
  "motel",
  "hostel",
  "lodging",
  "resort_hotel",
  "inn",
  "supermarket",
  "shopping_mall",
  "department_store",
  "grocery_store",
  "convenience_store",
  "wholesaler",
  "gas_station",
  "car_dealer",
  "car_rental",
  "car_repair",
  "car_wash",
  "tire_shop",
  "truck_dealer",
  "bank",
  "accounting",
  "pharmacy",
  "drugstore",
  "doctor",
  "dentist",
  "veterinary_care",
  "corporate_office",
  "business_center",
  "coworking_space",
  "manufacturer",
  "supplier",
  "farm",
  "ranch",
  "moving_company",
  "storage",
  "courier_service",
  "shipping_service",
  "hair_salon",
  "beauty_salon",
  "barber_shop",
  "spa",
  "nail_salon",
  "real_estate_agency",
  "insurance_agency",
  "travel_agency",
  "florist",
  "laundry",
  "locksmith",
])

const isBusinessPlace = (types: string[]): boolean => {
  return types.some(
    (type) =>
      BUSINESS_TYPES.has(type) ||
      type.endsWith("_restaurant") ||
      type.endsWith("_store") ||
      type.endsWith("_shop") ||
      type.endsWith("_agency"),
  )
}

const convertPlaceToLocation = (
  place: google.maps.places.Place,
  types: string[],
): Location => {
  const location: Location = {
    lat: place.location?.lat()?.toString(),
    lon: place.location?.lng()?.toString(),
    formattedAddress: place.formattedAddress || place.displayName || "",
  }

  // Extract additional address components if available. Street is assembled
  // from `route` (street name) + `street_number`; many picks (e.g. a city)
  // have no route at all, in which case street stays undefined and consumers
  // fall back to the formatted address.
  let route: string | undefined
  let streetNumber: string | undefined
  place.addressComponents?.forEach((component) => {
    component.types.forEach((type) => {
      if (type === "country") {
        location.countryCode = component.shortText || undefined
        location.country = component.longText || undefined
      }
      if (type === "locality") {
        location.locality = component.longText || undefined
      }
      if (type === "postal_code") {
        location.postalCode = component.longText || undefined
      }
      if (type === "route") {
        route = component.longText || undefined
      }
      if (type === "street_number") {
        streetNumber = component.longText || undefined
      }
    })
  })

  if (route) {
    location.street = streetNumber ? `${route} ${streetNumber}` : route
  }

  if (isBusinessPlace(types)) {
    location.companyName = place.displayName || undefined
  }

  return location
}

export const AutocompleteGooglePlaces = ({
  onLocationSelect,
  onPlaceSelect,
  onAbort,
  initialValue = "",
  placeholder,
  autoFocus = false,
}: AutocompleteGooglePlacesProps) => {
  const { t } = useTranslation()
  const places = useMapsLibrary("places")
  const inputRef = useRef<HTMLInputElement>(null)
  const popoverRef = useRef<HTMLDivElement>(null)
  const popupObserverRef = useRef<ResizeObserver | null>(null)
  const [inputValue, setInputValue] = useState<string>(initialValue)
  const [displayValue, setDisplayValue] = useState<string>(initialValue)

  const [debouncedInputValue] = useDebounce(inputValue, 400)

  // Analytics tracking
  const [suggestionsRequestCount, setSuggestionsRequestCount] =
    useState<number>(0)
  const [selectedSuggestion, setSelectedSuggestion] = useState<string | null>(
    null,
  )
  const componentStartTime = useRef<number>(Date.now())
  // Czy jest CO zgłosić — czy trwa interakcja z tym polem, którą da się
  // zakończyć. Nasłuch mousedown wisi na document przez całe życie komponentu, a
  // w planerze komponent jest zamontowany całą sesję dyspozytora: bez tego
  // znacznika każde kliknięcie gdziekolwiek w aplikacji szło do Segmenta jako
  // płatne zdarzenie.
  const hasLiveInteractionRef = useRef<boolean>(false)

  const [selectedIndex, setSelectedIndex] = useState<number>(-1)
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const [showAbove, setShowAbove] = useState<boolean>(false)
  const { suggestions, resetSession } =
    useAutocompleteSuggestions(debouncedInputValue)

  // Track suggestions requests when debounced input changes
  useEffect(() => {
    if (debouncedInputValue.trim()) {
      setSuggestionsRequestCount((prev) => prev + 1)
    }
  }, [debouncedInputValue])

  // Zamknięcie pola zgłaszamy raz na interakcję. Wszystkie trzy drogi wyjścia
  // (klik obok, blur, Escape) idą tędy, żeby ten warunek był jeden.
  const trackSessionEnd = useCallback(() => {
    if (!hasLiveInteractionRef.current) return
    hasLiveInteractionRef.current = false
    if (!window.analytics) return
    window.analytics.track("AutocompleteGooglePlaces", {
      suggestionsRequestCount,
      userSelectedSuggestion: !!selectedSuggestion,
      selectedOption: selectedSuggestion,
      sessionDuration: Date.now() - componentStartTime.current,
    })
  }, [suggestionsRequestCount, selectedSuggestion])

  const parseCoordinates = useCallback((input: string): string | null => {
    const regex = /(-?\d+[.,]?\d*)\s*[,\s]+\s*(-?\d+[.,]?\d*)/
    const match = input.match(regex)

    if (match) {
      const latStr = match[1].replace(",", ".")
      const lonStr = match[2].replace(",", ".")

      const lat = parseFloat(latStr)
      const lon = parseFloat(lonStr)

      if (!isNaN(lat) && !isNaN(lon)) {
        return `${latStr}, ${lonStr}`
      }
    }

    return null
  }, [])

  const checkPosition = useCallback(() => {
    if (!inputRef.current || !popoverRef.current) return

    const inputRect = inputRef.current.getBoundingClientRect()
    const popoverRect = popoverRef.current.getBoundingClientRect()
    const viewportHeight = window.innerHeight
    const spaceBelow = viewportHeight - inputRect.bottom - 12
    const spaceAbove = inputRect.top - 12
    const popoverHeight = popoverRect.height

    // Determine if we should show above based on available space
    const shouldShowAbove =
      spaceBelow < popoverHeight && spaceAbove >= popoverHeight

    setShowAbove(shouldShowAbove)

    // Set the exact position now that we have the actual popover dimensions
    if (popoverRef.current) {
      popoverRef.current.style.top = shouldShowAbove
        ? `${inputRect.top - popoverHeight - 8}px`
        : `${inputRect.bottom + 8}px`
    }
  }, [])

  const handleInput = useCallback((event: FormEvent<HTMLInputElement>) => {
    const newValue = (event.target as HTMLInputElement).value
    setInputValue(newValue)
    setDisplayValue(newValue)
    setIsOpen(true)
    setSelectedIndex(-1)
    hasLiveInteractionRef.current = true
  }, [])

  const handleSuggestionClick = useCallback(
    async (suggestion: google.maps.places.AutocompleteSuggestion) => {
      if (!places) return
      if (!suggestion.placePrediction) return

      const place = suggestion.placePrediction.toPlace()
      console.log("[DEBUG] Fetching fields")
      await place.fetchFields({
        fields: [
          "viewport",
          "location",
          "formattedAddress",
          "displayName",
          "addressComponents",
        ],
      })

      const selectedText = suggestion.placePrediction.text.text || ""
      setInputValue(selectedText)
      setDisplayValue(selectedText)
      setIsOpen(false)
      setSelectedSuggestion(selectedText)

      // calling fetchFields invalidates the session-token, so we now have to call
      // resetSession() so a new one gets created for further search
      console.log("[DEBUG] Reseting session")
      resetSession()

      if (window.analytics) {
        window.analytics.track("AutocompleteGooglePlaces", {
          suggestionsRequestCount,
          userSelectedSuggestion: true,
          selectedOption: selectedText,
          sessionDuration: Date.now() - componentStartTime.current,
        })
      }
      // Wybór JEST zakończeniem interakcji i sam się zgłosił — kolejne
      // kliknięcie obok pola nie ma dopisywać drugiego zdarzenia.
      hasLiveInteractionRef.current = false
      onLocationSelect?.(
        convertPlaceToLocation(place, suggestion.placePrediction.types),
      )
      onPlaceSelect?.(place)

      // Keep focus on input
      inputRef.current?.focus()
    },
    [places, resetSession, onLocationSelect, suggestionsRequestCount],
  )

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      switch (event.key) {
        case "ArrowDown":
          if (!suggestions.length) return
          event.preventDefault()
          setSelectedIndex((prev) => {
            if (prev === -1) {
              // First navigation: always select first item (top of list)
              const newIndex = 0
              setDisplayValue(
                suggestions[newIndex].placePrediction?.text.text || "",
              )
              return newIndex
            }

            // ArrowDown always moves to next item (or wraps to first)
            const newIndex = prev >= suggestions.length - 1 ? 0 : prev + 1
            setDisplayValue(
              suggestions[newIndex].placePrediction?.text.text || "",
            )
            return newIndex
          })
          break
        case "ArrowUp":
          if (!suggestions.length) return
          event.preventDefault()
          setSelectedIndex((prev) => {
            if (prev === -1) {
              // First navigation: always select last item (bottom of list)
              const newIndex = suggestions.length - 1
              setDisplayValue(
                suggestions[newIndex].placePrediction?.text.text || "",
              )
              return newIndex
            }

            // ArrowUp always moves to previous item (or wraps to last)
            const newIndex = prev <= 0 ? suggestions.length - 1 : prev - 1
            setDisplayValue(
              suggestions[newIndex].placePrediction?.text.text || "",
            )
            return newIndex
          })
          break
        case "Enter":
          event.preventDefault()
          if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
            handleSuggestionClick(suggestions[selectedIndex])
          } else {
            // Check if input is coordinates
            const coordinates = parseCoordinates(inputValue)
            if (coordinates) {
              const [lat, lon] = coordinates
                .split(", ")
                .map((coord) => parseFloat(coord))
              const location: Location = {
                lat: lat.toString(),
                lon: lon.toString(),
                formattedAddress: coordinates,
              }

              setSelectedSuggestion(coordinates)
              setIsOpen(false)
              onLocationSelect?.(location)

              if (window.analytics) {
                window.analytics.track("AutocompleteGooglePlaces", {
                  suggestionsRequestCount,
                  userSelectedSuggestion: false,
                  selectedOption: coordinates,
                  sessionDuration: Date.now() - componentStartTime.current,
                })
              }
              hasLiveInteractionRef.current = false

              // Keep focus on input
              inputRef.current?.focus()
            }
          }
          break
        case "Escape":
          event.preventDefault()
          setIsOpen(false)
          setDisplayValue(inputValue) // Restore original input value
          setSelectedIndex(-1)
          console.log("[DEBUG] Reseting session")
          resetSession()

          // Send analytics event when component closes via Escape
          trackSessionEnd()

          onAbort?.()
          break
      }
    },
    [
      suggestions,
      selectedIndex,
      inputValue,
      resetSession,
      onAbort,
      handleSuggestionClick,
      parseCoordinates,
      onLocationSelect,
      suggestionsRequestCount,
      trackSessionEnd,
    ],
  )

  // Set up the observer when popover is shown
  useEffect(() => {
    if (isOpen && suggestions.length > 0) {
      // Initialize position calculation
      setTimeout(checkPosition, 0)

      // Create resize observer for popover
      if (!popupObserverRef.current && popoverRef.current) {
        popupObserverRef.current = new ResizeObserver(() => {
          checkPosition()
        })
        popupObserverRef.current.observe(popoverRef.current)
      }
    }

    return () => {
      if (popupObserverRef.current) {
        popupObserverRef.current.disconnect()
        popupObserverRef.current = null
      }
    }
  }, [isOpen, suggestions.length, checkPosition])

  // Handle window resize events
  useEffect(() => {
    if (isOpen) {
      window.addEventListener("resize", checkPosition)
      return () => window.removeEventListener("resize", checkPosition)
    }
  }, [isOpen, checkPosition])

  const handleClickOutside = useCallback(
    (event: MouseEvent) => {
      const target = event.target as HTMLElement

      // Check if clicking outside the input element (regardless of popover state)
      if (inputRef.current && !inputRef.current.contains(target)) {
        // If popover is open, also check if clicking outside popover
        if (
          isOpen &&
          popoverRef.current &&
          popoverRef.current.contains(target)
        ) {
          return // Don't close if clicking inside popover
        }

        setIsOpen(false)
        setDisplayValue(inputValue) // Restore original input value
        setSelectedIndex(-1)
        resetSession()

        // Send analytics event when component closes — tylko jeśli była
        // interakcja do zakończenia (patrz trackSessionEnd).
        trackSessionEnd()

        onAbort?.()
      }
    },
    [isOpen, inputValue, resetSession, onAbort, trackSessionEnd],
  )

  // Handle clicking outside to close edit mode (always active when component is mounted)
  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [handleClickOutside])

  return (
    <div className="relative w-full">
      <input
        ref={inputRef}
        className="h-[31px] w-full rounded-md border px-2 py-1 dark:bg-input/30"
        value={displayValue}
        onInput={handleInput}
        onKeyDown={handleKeyDown}
        onFocus={(e) => {
          if (suggestions.length > 0) {
            setIsOpen(true)
            // Powrót do pola z gotowymi podpowiedziami to wznowiona interakcja.
            hasLiveInteractionRef.current = true
          }
          e.target.select()
        }}
        onBlur={(e) => {
          // Delay closing to allow clicking on suggestions
          setTimeout(() => {
            if (
              e.currentTarget &&
              !e.currentTarget.contains(document.activeElement)
            ) {
              setIsOpen(false)
              setDisplayValue(inputValue) // Restore original input value
              setSelectedIndex(-1)

              // Send analytics event when component closes via blur — tak samo
              // warunkowo jak klik obok: samo wejście i wyjście z pola bez
              // wpisania czegokolwiek nie jest zdarzeniem do zgłoszenia.
              trackSessionEnd()
            }
          }, 150)
        }}
        placeholder={
          placeholder ?? t("maps.autocompleteGooglePlaces.placeholder")
        }
        autoFocus={autoFocus}
      />
      {isOpen &&
        suggestions.length > 0 &&
        createPortal(
          <div
            ref={popoverRef}
            className="fixed z-[200] shadow-lg"
            style={{
              width: inputRef.current?.getBoundingClientRect().width,
              left: inputRef.current?.getBoundingClientRect().left,
              // Initial position - will be adjusted by observer
              top: showAbove
                ? (inputRef.current?.getBoundingClientRect()?.top || 0) - 300 // temporary initial value
                : (inputRef.current?.getBoundingClientRect()?.bottom || 0) + 8,
              maxHeight: "300px",
            }}
          >
            <ul
              className="max-h-[300px] w-full overflow-y-auto rounded-md border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-900"
              onMouseLeave={() => {
                setSelectedIndex(-1)
                setDisplayValue(inputValue)
              }}
            >
              {suggestions.map((suggestion, index) => {
                const Icon = getPlaceTypeIcon(
                  suggestion.placePrediction?.types || [],
                )
                return (
                  <li
                    key={index}
                    className={`flex cursor-pointer items-center gap-2 px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 ${
                      index === selectedIndex
                        ? "bg-gray-100 dark:bg-gray-700"
                        : ""
                    }`}
                    onMouseDown={(e) => {
                      // Prevent input from losing focus
                      e.preventDefault()
                    }}
                    onClick={() => handleSuggestionClick(suggestion)}
                    onMouseEnter={() => {
                      setSelectedIndex(index)
                      setDisplayValue(
                        suggestion.placePrediction?.text.text || "",
                      )
                    }}
                  >
                    <Icon className="size-4 shrink-0 text-muted-foreground" />
                    <span className="truncate">
                      {suggestion.placePrediction?.text.text}
                    </span>
                  </li>
                )
              })}
            </ul>
          </div>,
          document.body,
        )}
    </div>
  )
}
